import React, { useEffect, useState } from "react";
import { observer, useLocalStore } from "mobx-react";
import { Button, Checkbox, Collapse, Spin, Tooltip } from "antd";
import { PlayCircleOutlined } from "@ant-design/icons";

import BaseModal from "@app/components/modal";
import confirm from "@app/components/confirm";
import http from "@app/lib/http";
import cerConfigStore from "@app/state/store/cer-config";
import { aiTemplateLabel, aiTemplateShortName } from "@app/constants";

import { GENERAL_SECTION, fromConfig, mergeFromConfig, toPayload } from "./fields";
import { resolveTemplate } from "./templates";
import FieldList from "./field-list";
import SectionPanel from "./section-panel";
import CopyFrom from "./copy-from";

import "./style.scoped.scss";
import "./modal.scss";

// Checkboxes over the section catalog — step 1. Deselecting a section keeps
// everything assigned to it: what is stored is a superset, and this list is the
// record of which of it to generate.
//
// Never rendered for a template whose sections are fixed: it has no step for
// this, because there is nothing to choose.
const SectionSelect = observer(({ template, values, setValue }) => {
    const selected = Array.isArray(values.sections_selected) ? values.sections_selected : [];
    const selectedSet = new Set(selected);

    const toggle = (key) => {
        setValue(
            "sections_selected",
            selectedSet.has(key) ? selected.filter((k) => k !== key) : [...selected, key],
        );
    };

    const hasStoredConfig = (key) =>
        Boolean(
            (values.section_documents?.[key] || []).length ||
                (values.section_articles?.[key] || []).length ||
                (values.section_priority?.[key] || []).length,
        );

    return (
        <div className="cer-section-select">
            <div className="intro">
                Choose what to generate. The next step asks only for the input each of these
                sections needs. Configuration for a section is never lost when you leave it
                unticked, so you can regenerate one section at a time.
            </div>
            {template.sectionCatalog.map((s) => (
                <div className="section-row" key={s.key} onClick={() => toggle(s.key)}>
                    <Checkbox
                        checked={selectedSet.has(s.key)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggle(s.key)}
                    />
                    <span className="label">{s.label}</span>
                    {hasStoredConfig(s.key) && (
                        <span className="retained">Previous configuration available</span>
                    )}
                </div>
            ))}
            {selected.length === 0 && (
                <div className="empty">Nothing selected — no sections will be generated.</div>
            )}
        </div>
    );
});

const CerConfigModal = observer(() => {
    const localState = useLocalStore(() => ({
        values: {},
        initializedFor: null,
        // Which accordion panels the user has explicitly collapsed. Tracked
        // inverted so a newly-selected section opens by default.
        collapsed: [],
    }));

    // `null` until the fetch resolves — distinct from "loaded, and empty". Saving
    // while it is null must not prune the stored assignments against an empty set.
    const [documents, setDocuments] = useState(null);
    // The project's L2-included articles, per channel, on the same convention.
    // Fetched once here rather than per panel, like the documents: there is one
    // panel per section and both channels are small.
    const [articles, setArticles] = useState({ slr: null, sota: null });
    const projectId = cerConfigStore.projectId;
    const reportDocumentId = cerConfigStore.reportDocumentId;
    const visible = cerConfigStore.visible;
    // The catalog behind this document's template. Everything below reads its
    // sections and fields off this rather than importing them.
    const template = resolveTemplate(cerConfigStore.template);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            // Back to "not loaded" first: reopening for a different project must
            // not show the previous one's documents while the fetch is in flight.
            setDocuments(null);
            if (!projectId || !visible) {
                return;
            }
            try {
                const { data } = await http.get(`/project/${projectId}/documents`);
                const list = (data?.list || []).map((d) => ({
                    _id: d._id,
                    name: d.name,
                    hasPdf: d.file?.mime === "application/pdf" || !!d.preview,
                    previewStatus: d.previewStatus,
                }));
                if (!cancelled) {
                    setDocuments(list);
                }
            } catch (ex) {
                if (!cancelled) {
                    setDocuments([]);
                }
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [projectId, visible]);

    useEffect(() => {
        let cancelled = false;
        async function loadChannel(type) {
            try {
                const {
                    data,
                } = await http.get(
                    `/project/${projectId}/report-documents/${reportDocumentId}/cer/articles`,
                    { type },
                );
                return data?.list || [];
            } catch (ex) {
                return [];
            }
        }
        async function load() {
            setArticles({ slr: null, sota: null });
            if (!projectId || !reportDocumentId || !visible) {
                return;
            }
            // A template with no article-scoped sections has nothing to fetch.
            // Resolved rather than left null: null is the "still loading" sentinel
            // and would leave a panel sitting on a spinner forever.
            if (!template.hasArticles) {
                setArticles({ slr: [], sota: [] });
                return;
            }
            const [slr, sota] = await Promise.all([loadChannel("slr"), loadChannel("sota")]);
            if (!cancelled) {
                setArticles({ slr, sota });
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [projectId, reportDocumentId, visible, template.id, template.hasArticles]);

    if (!visible) {
        return null;
    }

    // Sync local form values with the loaded config once per open() cycle. The
    // document id is part of the key because a project can hold several AI
    // documents: without it, opening B after A would keep A's form values.
    const cycleKey = `${projectId}:${cerConfigStore.reportDocumentId}:${
        cerConfigStore.config?.updated || ""
    }`;
    if (localState.initializedFor !== cycleKey) {
        localState.values = fromConfig(cerConfigStore.config, template);
        localState.initializedFor = cycleKey;
    }

    const setValue = (name, value) => {
        localState.values = { ...localState.values, [name]: value };
    };

    const applySource = (config, sectionKey = null) => {
        localState.values = mergeFromConfig(localState.values, config, template, { sectionKey });
    };

    const steps = template.steps;
    // Clamped, not read straight: the store's step index survives a close, and a
    // template with fewer steps than the last one would index past the end.
    const stepIndex = Math.min(cerConfigStore.currentStepIndex, steps.length - 1);
    const currentStep = steps[stepIndex];
    const isLastStep = stepIndex === steps.length - 1;

    const selectedSet = new Set(
        Array.isArray(localState.values.sections_selected)
            ? localState.values.sections_selected
            : [],
    );
    // Display order follows the catalog, not the order the user ticked them.
    const selectedSections = template.sectionCatalog
        .filter((s) => selectedSet.has(s.key))
        .map((s) => s.key);

    const onCancel = () => cerConfigStore.close();
    const goPrev = () => cerConfigStore.setStep(Math.max(0, stepIndex - 1));
    const goNext = () => cerConfigStore.setStep(Math.min(steps.length - 1, stepIndex + 1));

    // A list that is still null means "not loaded", and must not be used to prune
    // the stored assignments — `toPayload` skips the filter for a null argument.
    const knownArticleIds =
        articles.slr && articles.sota
            ? [...articles.slr, ...articles.sota].map((a) => a._id)
            : null;

    const onSave = async () => {
        const payload = toPayload(
            localState.values,
            documents && documents.map((d) => d._id),
            knownArticleIds,
            template,
        );
        const ok = await cerConfigStore.save(payload);
        if (ok) {
            localState.initializedFor = null;
        }
        return ok;
    };

    const onSaveAndRun = async () => {
        const ok = await onSave();
        if (!ok) {
            return;
        }
        if (!cerConfigStore.canStartRun) {
            // The banners already explain why — nothing more to do.
            return;
        }
        const shortName = aiTemplateShortName(cerConfigStore.template);
        const rerun = cerConfigStore.hasPreviousRun;
        const proceed = await confirm(
            rerun ? (
                <div>
                    <p>
                        This will regenerate the sections you selected and replace their current
                        content.
                    </p>
                    <ul style={{ listStyle: "disc", paddingLeft: 20 }}>
                        <li>Edits to the sections being regenerated will be lost.</li>
                        <li>Sections you did not select keep their content.</li>
                        <li>Manually added sections will be preserved.</li>
                    </ul>
                    <br />
                    <p>
                        <b>NOTE</b>: This action cannot be undone
                    </p>
                </div>
            ) : (
                `Start a new ${shortName} generation run for this project?`
            ),
            {
                type: rerun ? "danger" : "default",
                title: rerun ? `Re-run ${shortName} generation?` : `Start ${shortName} run`,
            },
        );
        if (proceed) {
            const run = await cerConfigStore.startRun();
            if (run) {
                cerConfigStore.close();
            }
        }
    };

    const missing = cerConfigStore.validation?.missing || [];
    const canRun = cerConfigStore.canStartRun;

    const runTooltip = canRun ? (
        `Save the form and start a ${aiTemplateShortName(cerConfigStore.template)} run.`
    ) : (
        <div className="cer-run-blocked-tip">
            {cerConfigStore.startRunBlockedReason}
            {missing.length > 0 && (
                <ul>
                    {missing.map((m) => (
                        <li key={m.field}>{m.label}</li>
                    ))}
                </ul>
            )}
        </div>
    );

    const sectionLabel = (key) => template.sectionCatalog.find((s) => s.key === key)?.label || key;

    const panelKeys = [GENERAL_SECTION, ...selectedSections];
    const activeKeys = panelKeys.filter((k) => !localState.collapsed.includes(k));

    const footer = (
        <div className="wizard-foot">
            <div className="left">
                <Button onClick={onCancel}>Close</Button>
            </div>
            <div className="right">
                <Button disabled={stepIndex === 0} onClick={goPrev}>
                    Previous
                </Button>
                <Tooltip title="Saved data is not lost between steps.">
                    <Button loading={cerConfigStore.saving} onClick={onSave}>
                        Save
                    </Button>
                </Tooltip>
                {!isLastStep && (
                    <Button type="primary" onClick={goNext}>
                        Next
                    </Button>
                )}
                {isLastStep && (
                    <Tooltip title={runTooltip} overlayClassName="cer-run-blocked-tooltip">
                        <Button
                            type="primary"
                            icon={<PlayCircleOutlined />}
                            disabled={!canRun}
                            loading={cerConfigStore.saving || cerConfigStore.running}
                            onClick={onSaveAndRun}
                        >
                            Save &amp; Start Run
                        </Button>
                    </Tooltip>
                )}
            </div>
        </div>
    );

    return (
        <BaseModal
            title={`${aiTemplateLabel(cerConfigStore.template)} — ${currentStep.title}`}
            visible={true}
            centered
            width={900}
            wrapClassName="cer-config-modal"
            onCancel={onCancel}
            footer={footer}
        >
            <Spin spinning={cerConfigStore.busy}>
                <div className="wizard">
                    <div className="wizard-head">
                        {/* A one-step template has nothing to navigate. */}
                        {steps.length > 1 && (
                            <div className="step-indicator">
                                {steps.map((s, i) => (
                                    <div
                                        key={s.key}
                                        className={`step-pill${i === stepIndex ? " active" : ""}`}
                                        onClick={() => cerConfigStore.setStep(i)}
                                    >
                                        {i + 1}. {s.title}
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="wizard-actions">
                            <CopyFrom template={template} onApply={(c) => applySource(c)} />
                        </div>

                        {cerConfigStore.draft && (
                            <div className="draft-banner">
                                <b>This project is still a draft.</b> You can fill in and save the
                                configuration now. Publish the project and upload source documents
                                to start generation.
                            </div>
                        )}
                    </div>

                    <div className="wizard-body">
                        {currentStep.key === "sections" ? (
                            <SectionSelect
                                template={template}
                                values={localState.values}
                                setValue={setValue}
                            />
                        ) : (
                            <Collapse
                                activeKey={activeKeys}
                                onChange={(keys) => {
                                    const open = new Set([].concat(keys));
                                    localState.collapsed = panelKeys.filter((k) => !open.has(k));
                                }}
                            >
                                <Collapse.Panel key={GENERAL_SECTION} header="General Info">
                                    <FieldList
                                        template={template}
                                        section={GENERAL_SECTION}
                                        values={localState.values}
                                        setValue={setValue}
                                        selectedSections={selectedSections}
                                    />
                                </Collapse.Panel>
                                {selectedSections.map((key) => (
                                    <Collapse.Panel key={key} header={sectionLabel(key)}>
                                        <SectionPanel
                                            template={template}
                                            sectionKey={key}
                                            values={localState.values}
                                            setValue={setValue}
                                            documents={documents}
                                            articles={articles}
                                            selectedSections={selectedSections}
                                            onCopyFrom={(c) => applySource(c, key)}
                                        />
                                    </Collapse.Panel>
                                ))}
                            </Collapse>
                        )}
                    </div>
                </div>
            </Spin>
        </BaseModal>
    );
});

export default CerConfigModal;
